import { useLocation } from 'react-router-dom';
import { useCallback } from 'react';

const LANGS = ['cs', 'en', 'de', 'hu', 'pl', 'fr'];

/** Routes carry an optional [lang] prefix (/en/..., /cs/...); strip it before matching. */
export const stripLang = (pathname: string) => {
  const seg = pathname.split('/')[1];
  return LANGS.includes(seg) ? '/' + pathname.split('/').slice(2).join('/') : pathname;
};

/** Drops the query string and any trailing slash so two spellings of the same route compare equal. */
const normalize = (url: string) => {
  const path = stripLang((url ?? '').split('?')[0]);
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
};

/**
 * How well `url` matches `pathname`: -1 for no match, otherwise the length of the matched prefix so
 * callers can pick the most specific entry. A match is either the exact path or a descendant of it —
 * the `/` boundary is what keeps `/customer/expeditions` from claiming `/customer/expedition/1`.
 */
export const matchScore = (url: string, pathname: string): number => {
  if (!url) return -1;
  const target = normalize(url);
  const current = normalize(pathname);
  if (target === '/' || target === '') return current === '/' || current === '' ? 1 : -1;
  if (current === target) return target.length;
  return current.startsWith(target + '/') ? target.length : -1;
};

/**
 * Route matching for navigation chrome. `isRouteActive` keeps the loose prefix semantics the mobile
 * bar relies on (it matches on path + search); `score` is the strict variant used to pick a single
 * winning menu entry.
 */
export const useActiveRoute = () => {
  const location = useLocation();

  const isRouteActive = useCallback((path: string) => (stripLang(location.pathname) + location.search).startsWith(path), [location.pathname, location.search]);

  const isHomeActive = useCallback(() => {
    const p = stripLang(location.pathname);
    return p === '/' || p === '';
  }, [location.pathname]);

  const score = useCallback((url: string) => matchScore(url, location.pathname), [location.pathname]);

  return { pathname: location.pathname, stripLang, isRouteActive, isHomeActive, score };
};

export default useActiveRoute;
